import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Typography, Card, CardContent, Button, Stack, Divider, CircularProgress } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import { initMRZReader } from 'web-mrz-reader';

import AuthService from '../services/auth';


function IdScanner({ setNavbarState, navbarState }) {
    const navigate = useNavigate();
    const [redirect, setRedirect] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [result, setResult] = useState(null);
    const [scanning, setScanning] = useState(true);
    const [processing, setProcessing] = useState(false);

    const containerRef = useRef(null);
    const readerRef = useRef(null);

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    useEffect(() => {
        if (AuthService.getCurrentUserRole() !== 'superuser') {
            setRedirect(true);
            return;
        }
        setNavbarState({ ...navbarState, showBack: true, backTo: '/adminhub', showOptions: false, showSearch: false });
        return () => {
            if (readerRef.current) {
                try { readerRef.current.stop(); } catch (e) { /* noop */ }
                readerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!scanning) return;
        if (!containerRef.current) return;

        let cancelled = false;

        const reader = initMRZReader({
            container: containerRef.current,
            workerPath: '/tesseract/worker.min.js',
            corePath: '/tesseract/',
            langPath: '/model/',
            onResult: (data) => {
                if (cancelled) return;
                setResult(data);
                setScanning(false);
                setProcessing(false);
            },
            onError: (error) => {
                if (cancelled) return;
                setSnackbar({ open: true, message: typeof error === 'string' ? error : 'Error al leer el MRZ.', severity: 'error' });
                setProcessing(false);
            },
        });
        readerRef.current = reader;

        setTimeout(() => {
            if (cancelled || !containerRef.current) return;
            const wrapper = containerRef.current.firstChild;
            if (wrapper) {
                wrapper.style.position = 'absolute';
                wrapper.style.inset = '0';
                wrapper.style.width = '100%';
                wrapper.style.maxWidth = 'none';
                wrapper.style.height = '100%';
                wrapper.style.aspectRatio = 'auto';
                wrapper.style.borderRadius = '0';
                wrapper.style.border = 'none';
                const video = wrapper.querySelector('video');
                if (video) {
                    video.style.position = 'absolute';
                    video.style.inset = '0';
                    video.style.width = '100%';
                    video.style.height = '100%';
                    video.style.objectFit = 'cover';
                }
            }
        }, 0);

        return () => {
            cancelled = true;
            if (readerRef.current) {
                try { readerRef.current.stop(); } catch (e) { /* noop */ }
                readerRef.current = null;
            }
        };
    }, [scanning]);

    const handleCapture = () => {
        setProcessing(true);
        if (readerRef.current) {
            try { readerRef.current.capture(); } catch (e) { /* noop */ }
        }
    };

    const handleRescan = () => {
        setResult(null);
        setScanning(true);
    };

    const handleCancel = () => {
        if (readerRef.current) {
            try { readerRef.current.stop(); } catch (e) { /* noop */ }
            readerRef.current = null;
        }
        navigate('/adminhub');
    };

    if (redirect) {
        return <Navigate to="/adminhub" replace={true} />;
    }

    const parsed = result && result.parsed && typeof result.parsed === 'object' ? result.parsed : null;
    const validation = parsed && parsed.Validation ? parsed.Validation : null;

    const renderValidationRow = (label, valid) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">{label}</Typography>
            <Typography variant="body2" sx={{ color: valid ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                {valid ? '✓' : '✗'}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100dvh', bgcolor: scanning ? 'black' : 'background.default' }}>
            <Snackbar open={snackbar.open} autoHideDuration={8000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ mt: '3.2rem' }} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {scanning && (
                <Box
                    sx={{
                        position: 'relative',
                        height: '100dvh',
                        width: '100%',
                        overflow: 'hidden',
                    }}
                >
                    <div ref={containerRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />

                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            px: 2.5,
                            pt: 4,
                            pb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            zIndex: 2,
                        }}
                    >
                        <IconButton
                            onClick={handleCancel}
                            sx={{
                                bgcolor: 'rgba(0,0,0,0.45)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 36,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1.5,
                            zIndex: 2,
                            pointerEvents: 'none',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                bgcolor: 'rgba(0,0,0,0.35)',
                                px: 2,
                                py: 0.5,
                                borderRadius: '999px',
                                backdropFilter: 'blur(6px)',
                                textAlign: 'center',
                            }}
                        >
                            Enfoque la zona de lectura mecánica (MRZ) en el reverso del DPI
                        </Typography>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCapture}
                            disabled={processing}
                            startIcon={processing ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{
                                pointerEvents: 'auto',
                                borderRadius: '999px',
                                textTransform: 'none',
                            }}
                        >
                            {processing ? 'Procesando...' : 'Capturar'}
                        </Button>
                    </Box>
                </Box>
            )}

            {!scanning && result && (
                <Box sx={{ p: 2, pb: 8 }}>
                    <Card sx={{ mb: 2, borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="h6" gutterBottom>Datos del documento (MRZ)</Typography>
                            <Divider sx={{ mb: 1.5 }} />
                            {parsed ? (
                                <Stack spacing={1.5}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Tipo de Documento</Typography>
                                        <Typography variant="body1">{parsed['Document Type'] || 'No detectado'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">País emisor</Typography>
                                        <Typography variant="body1">{parsed['Issuing Country'] || 'No detectado'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Número de Documento (CUI)</Typography>
                                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{parsed['Document Number'] || 'No detectado'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Apellidos</Typography>
                                        <Typography variant="body1">{parsed['Surname'] || 'No detectado'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Nombres</Typography>
                                        <Typography variant="body1">{parsed['Given Names'] || 'No detectado'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Fecha de Nacimiento</Typography>
                                        <Typography variant="body1">{parsed['Date of Birth'] || 'No detectado'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Género</Typography>
                                        <Typography variant="body1">{parsed['Gender'] || 'No detectado'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Fecha de Vencimiento</Typography>
                                        <Typography variant="body1">{parsed['Expiration Date'] || 'No detectado'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Nacionalidad</Typography>
                                        <Typography variant="body1">{parsed['Nationality'] || 'No detectado'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Datos Opcionales</Typography>
                                        <Typography variant="body1">{parsed['Optional Data'] || 'No detectado'}</Typography>
                                    </Box>
                                </Stack>
                            ) : (
                                <Typography variant="body1" color="text.secondary">
                                    No se pudo parsear el MRZ.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    {validation && (
                        <Card sx={{ mb: 2, borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Typography variant="h6" gutterBottom>Validación</Typography>
                                <Divider sx={{ mb: 1.5 }} />
                                <Stack spacing={1}>
                                    {renderValidationRow('Número de documento', validation.documentNumber)}
                                    {renderValidationRow('Fecha de nacimiento', validation.dateOfBirth)}
                                    {renderValidationRow('Fecha de vencimiento', validation.expirationDate)}
                                    {renderValidationRow('Validación compuesta', validation.composite)}
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Estado general</Typography>
                                        <Typography variant="body1" sx={{ color: validation.isValid ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                                            {validation.isValid ? 'MRZ VÁLIDO' : 'MRZ INVÁLIDO'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    <Card sx={{ mb: 2, borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="h6" gutterBottom>Texto MRZ crudo</Typography>
                            <Divider sx={{ mb: 1.5 }} />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {result.raw || ''}
                            </Typography>
                        </CardContent>
                    </Card>

                    <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
                        <Button variant="contained" color="primary" onClick={handleRescan} sx={{ borderRadius: '999px', textTransform: 'none' }}>
                            Escanear de nuevo
                        </Button>
                        <Button variant="outlined" onClick={handleCancel} sx={{ borderRadius: '999px', textTransform: 'none' }}>
                            Cerrar
                        </Button>
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

export default IdScanner;
